import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faInfoCircle,
  faFilter,
  faCode,
  faCheck,
  faTimes,
  faUndo
} from '@fortawesome/free-solid-svg-icons';

/**
 * AuthSmashScopeFilters - Advanced scope selection component
 * Allows filtering endpoints by regex patterns, parameters, methods, and presets
 */
const AuthSmashScopeFilters = ({
  scopeFilters,
  onFiltersChange,
  totalEndpoints = 0,
  filteredEndpoints = 0,
  isDarkMode = false,
  onReset // Callback to reset to defaults
}) => {
  const [expandedSections, setExpandedSections] = useState({
    presets: true,
    regex: false,
    parameters: false,
    methods: false
  });

  // Local state for regex patterns (arrays)
  const [includePatterns, setIncludePatterns] = useState(
    scopeFilters?.includePatterns?.join('\n') || ''
  );
  const [excludePatterns, setExcludePatterns] = useState(
    scopeFilters?.excludePatterns?.join('\n') || ''
  );

  // Preset definitions (can select multiple)
  const presets = [
    {
      id: 'exclude_static',
      label: 'Exclude Static',
      description: 'Filter out .js, .css, images, fonts',
      icon: faTimes
    },
    {
      id: 'sensitive_operations',
      label: 'Sensitive Operations',
      description: 'Only POST, PUT, DELETE, PATCH methods',
      icon: faFilter
    },
    {
      id: 'admin_endpoints',
      label: 'Admin Endpoints',
      description: 'URLs containing /admin/',
      icon: faCode
    },
    {
      id: 'user_endpoints',
      label: 'User Endpoints',
      description: 'URLs containing /user/',
      icon: faCode
    },
    {
      id: 'parameterized_only',
      label: 'Parameterized Only',
      description: 'URLs with query or path parameters',
      icon: faFilter
    }
  ];

  // HTTP methods
  const methods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS', 'TRACE'];

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handle preset toggle (multiple selection)
  const handlePresetToggle = (presetId) => {
    const currentPresets = scopeFilters?.usePresets || [];
    const newPresets = currentPresets.includes(presetId)
      ? currentPresets.filter(p => p !== presetId)
      : [...currentPresets, presetId];

    onFiltersChange({
      ...scopeFilters,
      usePresets: newPresets
    });
  };

  // Handle reset to defaults
  const handleReset = () => {
    if (onReset) {
      onReset();
    }
  };

  // Handle regex pattern changes (debounced)
  useEffect(() => {
    if (!onFiltersChange) return;

    const timer = setTimeout(() => {
      const includeArray = includePatterns
        .split('\n')
        .map(p => p.trim())
        .filter(p => p.length > 0);

      const excludeArray = excludePatterns
        .split('\n')
        .map(p => p.trim())
        .filter(p => p.length > 0);

      onFiltersChange({
        ...scopeFilters,
        includePatterns: includeArray,
        excludePatterns: excludeArray
      });
    }, 500); // Debounce 500ms

    return () => clearTimeout(timer);
  }, [includePatterns, excludePatterns, onFiltersChange, scopeFilters]);

  // Handle parameter filter changes
  const handleParameterFilterChange = (field, value) => {
    onFiltersChange({
      ...scopeFilters,
      parameterFilters: {
        ...(scopeFilters.parameterFilters || {}),
        [field]: value
      }
    });
  };

  // Handle parameter type toggle
  const handleParameterTypeToggle = (type) => {
    const currentTypes = scopeFilters?.parameterFilters?.parameterTypes || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter(t => t !== type)
      : [...currentTypes, type];

    handleParameterFilterChange('parameterTypes', newTypes);
  };

  // Handle method filter changes
  const handleMethodToggle = (method, isInclude) => {
    const filterKey = isInclude ? 'include' : 'exclude';
    const currentMethods = scopeFilters?.methodFilters?.[filterKey] || [];

    const newMethods = currentMethods.includes(method)
      ? currentMethods.filter(m => m !== method)
      : [...currentMethods, method];

    onFiltersChange({
      ...scopeFilters,
      methodFilters: {
        ...(scopeFilters.methodFilters || {}),
        [filterKey]: newMethods
      }
    });
  };

  return (
    <div className="space-y-2">
      {/* Filter Summary with Reset Button */}
      <div className={`border rounded p-2 ${
        isDarkMode
          ? 'bg-blue-900/20 border-blue-700'
          : 'bg-blue-50 border-blue-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faFilter} className="text-blue-400" />
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Scope Filters</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs">
              <span className={`font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{filteredEndpoints}</span>
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}> / {totalEndpoints} endpoints</span>
            </div>
            {onReset && (
              <button
                onClick={handleReset}
                className={`px-2 py-1 rounded text-xs flex items-center gap-1 transition-colors ${
                  isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                }`}
                title="Reset filters to default (exclude static assets)"
              >
                <FontAwesomeIcon icon={faUndo} className="text-[10px]" />
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Preset Filters */}
      <div className={`border rounded ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
        <button
          onClick={() => toggleSection('presets')}
          className={`w-full flex items-center justify-between p-2 rounded-t text-xs ${
            isDarkMode
              ? 'bg-gray-800 hover:bg-gray-750'
              : 'bg-gray-50 hover:bg-gray-100'
          }`}
        >
          <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Preset Filters</span>
          <FontAwesomeIcon
            icon={expandedSections.presets ? faChevronUp : faChevronDown}
            className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}
          />
        </button>

        {expandedSections.presets && (
          <div className="p-2 space-y-1">
            <p className={`text-[10px] mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
              Select multiple presets to combine filters
            </p>
            {presets.map(preset => {
              const isSelected = (scopeFilters?.usePresets || []).includes(preset.id);
              return (
                <button
                  key={preset.id}
                  onClick={() => handlePresetToggle(preset.id)}
                  className={`w-full text-left p-2 rounded border transition-all ${
                    isSelected
                      ? isDarkMode
                        ? 'border-blue-500 bg-blue-900/30'
                        : 'border-blue-500 bg-blue-50'
                      : isDarkMode
                      ? 'border-gray-700 hover:border-blue-500/50 hover:bg-gray-800'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <FontAwesomeIcon
                      icon={preset.icon}
                      className={`mt-0.5 text-xs ${
                        isSelected
                          ? 'text-blue-400'
                          : isDarkMode ? 'text-gray-500' : 'text-gray-400'
                      }`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{preset.label}</span>
                        {isSelected && (
                          <FontAwesomeIcon icon={faCheck} className="text-blue-400 text-xs" />
                        )}
                      </div>
                      <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>{preset.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Regex Patterns */}
      <div className={`border rounded ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
        <button
          onClick={() => toggleSection('regex')}
          className={`w-full flex items-center justify-between p-2 text-xs ${
            isDarkMode
              ? 'bg-gray-800 hover:bg-gray-750'
              : 'bg-gray-50 hover:bg-gray-100'
          }`}
        >
          <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Regex Patterns</span>
          <FontAwesomeIcon
            icon={expandedSections.regex ? faChevronUp : faChevronDown}
            className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}
          />
        </button>

        {expandedSections.regex && (
          <div className="p-2 space-y-2">
            {/* Include Patterns */}
            <div>
              <label className={`block text-[10px] font-medium mb-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`}>
                Include Patterns
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="ml-1 text-gray-500"
                  title="One regex pattern per line. Only URLs matching at least one pattern will be included."
                />
              </label>
              <textarea
                value={includePatterns}
                onChange={(e) => setIncludePatterns(e.target.value)}
                placeholder="Example:&#10;/api/admin/.*&#10;/api/users/\d+&#10;.*\?.*="
                className={`w-full px-2 py-1.5 border rounded text-xs font-mono ${
                  isDarkMode
                    ? 'bg-gray-900 border-gray-600 text-gray-300'
                    : 'bg-white border-gray-300 text-gray-700'
                }`}
                rows={3}
              />
              <p className={`text-[9px] mt-0.5 ${isDarkMode ? 'text-gray-600' : 'text-gray-500'}`}>
                One pattern per line. Example: /api/admin/.*
              </p>
            </div>

            {/* Exclude Patterns */}
            <div>
              <label className={`block text-[10px] font-medium mb-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`}>
                Exclude Patterns
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="ml-1 text-gray-500"
                  title="One regex pattern per line. URLs matching any pattern will be excluded."
                />
              </label>
              <textarea
                value={excludePatterns}
                onChange={(e) => setExcludePatterns(e.target.value)}
                placeholder="Example:&#10;/api/public/.*&#10;.*\.(js|css|png|jpg)$&#10;/health"
                className={`w-full px-2 py-1.5 border rounded text-xs font-mono ${
                  isDarkMode
                    ? 'bg-gray-900 border-gray-600 text-gray-300'
                    : 'bg-white border-gray-300 text-gray-700'
                }`}
                rows={3}
              />
              <p className={`text-[9px] mt-0.5 ${isDarkMode ? 'text-gray-600' : 'text-gray-500'}`}>
                One pattern per line. Example: .*\.(js|css)$
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Parameter Filters */}
      <div className={`border rounded ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
        <button
          onClick={() => toggleSection('parameters')}
          className={`w-full flex items-center justify-between p-2 text-xs ${
            isDarkMode
              ? 'bg-gray-800 hover:bg-gray-750'
              : 'bg-gray-50 hover:bg-gray-100'
          }`}
        >
          <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Parameter Filters</span>
          <FontAwesomeIcon
            icon={expandedSections.parameters ? faChevronUp : faChevronDown}
            className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}
          />
        </button>

        {expandedSections.parameters && (
          <div className="p-2 space-y-2">
            {/* Require Parameters Toggle */}
            <div className="flex items-center justify-between">
              <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`}>
                Only URLs with parameters
              </label>
              <input
                type="checkbox"
                checked={scopeFilters?.parameterFilters?.requireParameters || false}
                onChange={(e) => handleParameterFilterChange('requireParameters', e.target.checked)}
                className="h-3 w-3 text-blue-600 rounded"
              />
            </div>

            {/* Parameter Types */}
            {scopeFilters?.parameterFilters?.requireParameters && (
              <>
                <div>
                  <label className={`block text-[10px] font-medium mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-700'}`}>
                    Required Parameter Types
                  </label>
                  <div className="space-y-1">
                    {['query', 'body', 'path', 'header'].map(type => (
                      <label key={type} className="flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={(scopeFilters?.parameterFilters?.parameterTypes || []).includes(type)}
                          onChange={() => handleParameterTypeToggle(type)}
                          className="h-3 w-3 text-blue-600 rounded"
                        />
                        <span className={`text-xs capitalize ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`}>{type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Minimum Parameters */}
                <div>
                  <label className={`block text-[10px] font-medium mb-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-700'}`}>
                    Minimum Parameters
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={scopeFilters?.parameterFilters?.minParameters || 1}
                    onChange={(e) => handleParameterFilterChange('minParameters', parseInt(e.target.value))}
                    className={`w-full px-2 py-1 border rounded text-xs ${
                      isDarkMode
                        ? 'bg-gray-900 border-gray-600 text-gray-300'
                        : 'bg-white border-gray-300 text-gray-700'
                    }`}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Method Filters */}
      <div className={`border rounded ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
        <button
          onClick={() => toggleSection('methods')}
          className={`w-full flex items-center justify-between p-2 rounded-b text-xs ${
            isDarkMode
              ? 'bg-gray-800 hover:bg-gray-750'
              : 'bg-gray-50 hover:bg-gray-100'
          }`}
        >
          <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>HTTP Method Filters</span>
          <FontAwesomeIcon
            icon={expandedSections.methods ? faChevronUp : faChevronDown}
            className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}
          />
        </button>

        {expandedSections.methods && (
          <div className="p-2 space-y-2">
            {/* Include Methods */}
            <div>
              <label className={`block text-[10px] font-medium mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-700'}`}>
                Include Only These Methods
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="ml-1 text-gray-500"
                  title="If any method is selected, only those methods will be tested."
                />
              </label>
              <div className="flex flex-wrap gap-1">
                {methods.map(method => {
                  const isIncluded = (scopeFilters?.methodFilters?.include || []).includes(method);
                  return (
                    <button
                      key={method}
                      onClick={() => handleMethodToggle(method, true)}
                      className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all ${
                        isIncluded
                          ? 'bg-blue-500 text-white'
                          : isDarkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {method}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Exclude Methods */}
            <div>
              <label className={`block text-[10px] font-medium mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-700'}`}>
                Exclude These Methods
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="ml-1 text-gray-500"
                  title="These methods will be excluded from testing."
                />
              </label>
              <div className="flex flex-wrap gap-1">
                {methods.map(method => {
                  const isExcluded = (scopeFilters?.methodFilters?.exclude || []).includes(method);
                  return (
                    <button
                      key={method}
                      onClick={() => handleMethodToggle(method, false)}
                      className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all ${
                        isExcluded
                          ? 'bg-red-500 text-white'
                          : isDarkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {method}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthSmashScopeFilters;
